// The demos that run in the Tractor Supply skin. Each is its own crew and URL with the
// same pages (landing, customer list, call page) and the same demo customers.
// "tsc" is the main TSC CX crew (Riley); "tsc-mike" is a clone of it with a male voice
// (Mike), picked with the Riley / Mike toggle on the same pages; "multilingual-support-agent"
// is another clone that also speaks Spanish and Hindi, shown to other clients.

/** Who the page copy talks about: the agent's name, face and pronouns. */
export interface TscPersona {
  name: string;
  /** Portrait under public/. Until the file exists, the page shows the initial on TSC red. */
  avatar: string;
  they: "she" | "he";
  them: "her" | "him";
  their: "her" | "his";
}

export const RILEY: TscPersona = { name: "Riley", avatar: "/tsc/riley.png", they: "she", them: "her", their: "her" };
// No photo yet: /tsc/mike.png is to be made in Canva to match riley.png (red Tractor Supply polo).
export const MIKE: TscPersona = { name: "Mike", avatar: "/tsc/mike.png", they: "he", them: "him", their: "his" };

export interface TscDemo {
  /** AgentConfig.slug, the `?agent=` value on the customer list and call pages. */
  agentSlug: string;
  /** Landing page path. */
  home: string;
  /** The agent the copy names (keep in step with the AgentConfig's personaName and avatarUrl). */
  persona: TscPersona;
  /**
   * The Riley / Mike toggle: the agentSlugs of the same crew in each voice, this demo included,
   * default first. Absent: no toggle.
   */
  voices?: string[];
  /** Languages the agent speaks on this demo; shown on the landing when there is more than one. */
  languages: string[];
  /**
   * What the agent asks for first. "customerId": the customer ID + billing zip (the email only for a
   * password reset, or when the caller has no ID). "email": the email + billing zip.
   */
  verifyWith: "customerId" | "email";
  /**
   * The call page offers a box for the tester's own email. The crew's emails (password reset, payment link, order
   * confirmation) then go to that address instead of the Nurix inboxes; the crew reads it as `tester_email`.
   */
  testerEmail: boolean;
}

const TSC_VOICES = ["tsc", "tsc-mike"];

export const TSC_DEMOS: Record<string, TscDemo> = {
  tsc: { agentSlug: "tsc", home: "/nurix-experience/tsc", persona: RILEY, voices: TSC_VOICES, languages: ["English"], verifyWith: "customerId", testerEmail: true },
  // Mike shares Riley's landing page: /tsc?as=mike.
  "tsc-mike": { agentSlug: "tsc-mike", home: "/nurix-experience/tsc?as=mike", persona: MIKE, voices: TSC_VOICES, languages: ["English"], verifyWith: "customerId", testerEmail: true },
  "multilingual-support-agent": {
    agentSlug: "multilingual-support-agent",
    home: "/nurix-experience/multilingual-support-tsc",
    persona: RILEY,
    languages: ["English", "Spanish", "Hindi"],
    verifyWith: "email",
    testerEmail: false,
  },
};

export const DEFAULT_TSC_DEMO = TSC_DEMOS.tsc;

export function isTscSlug(slug?: string | null): boolean {
  return !!slug && slug in TSC_DEMOS;
}

export function tscDemoForSlug(slug?: string | null): TscDemo {
  return (slug && TSC_DEMOS[slug]) || DEFAULT_TSC_DEMO;
}

export function tscPickerHref(demo: TscDemo): string {
  return `/nurix-experience/experience?agent=${demo.agentSlug}`;
}

/** The demos on this demo's Riley / Mike toggle, default first; empty when it has none. */
export function tscVoices(demo: TscDemo): TscDemo[] {
  return (demo.voices ?? []).map((s) => TSC_DEMOS[s]).filter(Boolean);
}

/** The landing's `?as=` value for a voice ("mike"); undefined for the default voice. */
export function tscAsParam(demo: TscDemo): string | undefined {
  const voices = tscVoices(demo);
  return voices.length > 1 && voices[0] !== demo ? demo.persona.name.toLowerCase() : undefined;
}

/** The demo a landing page shows for its `?as=` value: a voice on its toggle, else the page's own demo. */
export function tscDemoForAs(demo: TscDemo, as?: string | null): TscDemo {
  const want = as?.trim().toLowerCase();
  return (want && tscVoices(demo).find((d) => d.persona.name.toLowerCase() === want)) || demo;
}
